import { useEffect, useState } from 'react';
import axios from 'axios';

/**
 * Categories (with subcategories) and UP mandals/districts, loaded once per page load
 * and shared by every component, so names are consistent in Hindi and English.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let categoriesPromise = null;
let regionsPromise = null;

function loadCategories() {
  if (!categoriesPromise) {
    categoriesPromise = axios.get(`${API}/categories`)
      .then((res) => res.data)
      .catch(() => {
        categoriesPromise = null; // retry on the next use
        return [];
      });
  }
  return categoriesPromise;
}

function loadRegions() {
  if (!regionsPromise) {
    regionsPromise = axios.get(`${API}/public/regions`)
      .then((res) => res.data)
      .catch(() => {
        regionsPromise = null;
        return { state: null, divisions: [] };
      });
  }
  return regionsPromise;
}

// Rajya/Sahar is shown on its own page (mandal → district filter), not as a plain category list
export const UP_CATEGORY = 'uttar-pradesh';
export const STATE_LEVEL = 'state';

export function useCategories() {
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    let alive = true;
    loadCategories().then((data) => alive && setCategories(data));
    return () => { alive = false; };
  }, []);

  const byId = Object.fromEntries(categories.map((c) => [c.id, c]));
  const topLevel = categories.filter((c) => !c.parent_id);
  const childrenOf = (id) => categories.filter((c) => c.parent_id === id);
  const label = (id, isHindi) => {
    const c = byId[id];
    if (!c) return id || '';
    return isHindi ? c.name_hi : c.name;
  };
  // Where a category's link should go
  const pathFor = (id) => (id === UP_CATEGORY ? '/rajya' : `/category/${id}`);

  return { categories, byId, topLevel, childrenOf, label, pathFor };
}

export function useRegions() {
  const [regions, setRegions] = useState({ state: null, divisions: [] });
  useEffect(() => {
    let alive = true;
    loadRegions().then((data) => alive && setRegions(data));
    return () => { alive = false; };
  }, []);

  const districtIndex = {};
  for (const div of regions.divisions) {
    for (const d of div.districts) districtIndex[d.id] = { ...d, division: div.id };
  }

  const districtLabel = (id, isHindi) => {
    if (!id) return '';
    if (id === STATE_LEVEL) return isHindi ? 'उत्तर प्रदेश' : 'Uttar Pradesh';
    const d = districtIndex[id];
    return d ? (isHindi ? d.name_hi : d.name) : '';
  };
  const divisionOf = (districtId) => districtIndex[districtId]?.division || null;
  // Link to a district's page on Rajya/Sahar
  const districtPath = (id) => {
    if (id === STATE_LEVEL) return `/rajya/${STATE_LEVEL}`;
    const div = divisionOf(id);
    return div ? `/rajya/${div}/${id}` : '/rajya';
  };

  return { regions, districtLabel, divisionOf, districtPath };
}
